import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Plus, Heart, Star, RotateCcw } from "lucide-react";
import { API, api, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const QUICK = [
  { key: "breakfast", icon: "🫒", label: "منتج للفطور", prompt: "ما المنتجات المناسبة للفطور من منتجاتكم؟" },
  { key: "gift", icon: "🎁", label: "هدية", prompt: "أريد هدية مميزة من منتجاتكم، ماذا تقترح؟" },
  { key: "palestinian", icon: "🇵🇸", label: "منتج فلسطيني أصيل", prompt: "أريد منتجًا فلسطينيًا أصيلًا من فلسطين." },
  { key: "diet", icon: "🌿", label: "نمط غذائي معين", prompt: "ما المنتجات الطبيعية أو العضوية المتوفرة لديكم؟" },
  { key: "coffee", icon: "☕", label: "قهوة وشاي", prompt: "ما المنتجات المناسبة مع القهوة والشاي؟" },
  { key: "new", icon: "✨", label: "اكتشاف شيء جديد", prompt: "اقترح عليّ منتجًا جديدًا أكتشفه من متجركم." },
];

const SKU_RE = /\[?([A-Z]{2}(?:-[A-Z0-9]{2}){2})\]?/g;

function ChatProductCard({ p }) {
  const { add } = useCart();
  const { user, isFavorite, toggleFavorite } = useAuth();
  return (
    <div data-testid={`ai-product-card-${p.sku}`} className="mt-2 flex gap-3 rounded-2xl border border-olive/10 bg-white p-2.5">
      <Link to={`/product/${p.slug}`} className="shrink-0">
        <img src={p.images?.main} alt={p.name_ar} loading="lazy" decoding="async" className="h-20 w-16 rounded-xl object-cover" />
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/product/${p.slug}`} className="line-clamp-1 font-display text-sm font-semibold text-ink hover:text-olive">{p.name_ar}</Link>
          <span className="shrink-0 font-display text-sm font-bold text-olive-dark">{fmtPrice(p.price)}</span>
        </div>
        {p.rating > 0 && (
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-ink/50">
            <Star className="h-3 w-3 fill-gold text-gold" />{p.rating}
          </div>
        )}
        <p className="mt-0.5 line-clamp-1 text-xs text-ink/50">{p.short_story || p.origin_text}</p>
        <div className="mt-2 flex items-center gap-1.5">
          <Link to={`/product/${p.slug}`} data-testid={`ai-product-view-${p.sku}`}
            className="rounded-full border border-olive/25 px-3 py-1 text-[11px] font-semibold text-olive-dark transition-colors hover:bg-olive/5">
            عرض المنتج
          </Link>
          <button onClick={() => add(p)} data-testid={`ai-product-add-${p.sku}`} aria-label="أضف إلى السلة"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-olive text-white transition-[background-color,transform] duration-200 hover:bg-olive-dark active:scale-90">
            <Plus className="h-3.5 w-3.5" />
          </button>
          {user && (
            <button onClick={() => toggleFavorite(p.sku)} data-testid={`ai-product-fav-${p.sku}`} aria-label="المفضلة"
              className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${isFavorite(p.sku) ? "bg-palred text-white" : "bg-olive/8 text-ink/60 hover:text-palred"}`}>
              <Heart className={`h-3.5 w-3.5 ${isFavorite(p.sku) ? "fill-current" : ""}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [productsBySku, setProductsBySku] = useState({});
  const [modelLabel, setModelLabel] = useState("");
  const lastUserText = useRef("");
  const scrollRef = useRef(null);
  const sessionId = useRef("s-" + Math.random().toString(36).slice(2));

  useEffect(() => {
    if (open && Object.keys(productsBySku).length === 0) {
      api.get("/products", { params: { limit: 100 } }).then((r) => {
        const map = {};
        r.data.forEach((p) => { map[p.sku] = p; });
        setProductsBySku(map);
      }).catch(() => {});
    }
    if (open && !modelLabel) {
      api.get("/settings/assistant").then((r) => setModelLabel(r.data.label)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    lastUserText.current = content;
    setInput("");
    const nextMsgs = [...messages, { role: "user", content }];
    setMessages([...nextMsgs, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: nextMsgs.map((m) => ({ role: m.role, content: m.content })),
          session_id: sessionId.current, lang: "ar",
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const l = line.trim();
          if (!l.startsWith("data:")) continue;
          const data = l.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              acc += parsed.delta;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: acc };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
      if (!acc) throw new Error("empty");
      const skus = [...new Set([...acc.matchAll(SKU_RE)].map((m) => m[1]))].filter((s) => productsBySku[s]);
      if (skus.length > 0) {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], products: skus.map((s) => productsBySku[s]) };
          return copy;
        });
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "", error: true };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const showQuick = messages.length === 0;

  return (
    <>
      <button
        data-testid="ai-assistant-toggle"
        onClick={() => setOpen((v) => !v)}
        className="btn-shine fixed bottom-5 left-5 z-[60] flex h-16 w-16 items-center justify-center rounded-full bg-olive text-ivory shadow-[0_12px_40px_-8px_rgba(44,62,45,0.6)] transition-transform duration-300 hover:scale-105 active:scale-95"
        aria-label="افتح رفيق سهول"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-7 w-7" />}
        {!open && <span className="absolute inset-0 -z-10 animate-pulse-ring rounded-full bg-olive/50" />}
      </button>

      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-24 left-5 z-[60] flex h-[min(580px,calc(100dvh-7.5rem))] w-[calc(100vw-2.5rem)] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-olive/15 bg-ivory shadow-2xl max-sm:left-3 max-sm:right-3 max-sm:bottom-20 max-sm:w-auto max-sm:max-w-none"
        >
          <div className="flex items-center gap-3 bg-olive-dark px-5 py-4 text-ivory">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ivory/15"><Sparkles className="h-5 w-5" /></span>
            <div>
              <div className="font-display font-semibold">رفيق سهول</div>
              <div className="text-[11px] text-ivory/70">مساعدك لاكتشاف المنتجات{modelLabel ? ` · ${modelLabel}` : ""}</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-ivory p-4">
            {showQuick && (
              <div>
                <div className="mb-3 rounded-2xl border border-olive/10 bg-white px-4 py-3 text-sm leading-relaxed text-ink">
                  أهلاً بك في سهول جنين 🌿 أنا رفيق سهول. <span className="font-semibold text-olive-dark">ماذا تبحث عنه؟</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK.map((qk) => (
                    <button key={qk.key} data-testid="ai-suggestion" onClick={() => send(qk.prompt)}
                      className="flex items-center gap-2 rounded-2xl border border-olive/15 bg-white px-3 py-2.5 text-right text-[13px] font-medium text-ink/80 transition-[border-color,background-color] duration-200 hover:border-olive/40 hover:bg-olive/5">
                      <span className="text-base leading-none">{qk.icon}</span> {qk.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[88%] ${m.role === "user"
                  ? "rounded-2xl bg-olive px-4 py-2.5 text-sm leading-relaxed text-ivory"
                  : "w-full max-w-[88%]"}`}>
                  {m.role === "user" ? m.content : (
                    m.error ? (
                      <div data-testid="ai-error" className="rounded-2xl border border-palred/20 bg-white px-4 py-3 text-sm text-ink/70">
                        تعذّر الاتصال بالمساعد حالياً.
                        <button data-testid="ai-retry" onClick={() => send(lastUserText.current)}
                          className="mt-2 flex items-center gap-1.5 rounded-full bg-olive px-4 py-1.5 text-xs font-semibold text-ivory">
                          <RotateCcw className="h-3.5 w-3.5" /> إعادة المحاولة
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-olive/10 bg-white px-4 py-2.5 text-sm leading-relaxed text-ink">
                        {m.content
                          ? <span className="whitespace-pre-wrap">{m.content}</span>
                          : <span className="inline-flex gap-1 py-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>}
                        {m.products?.map((p) => <ChatProductCard key={p.sku} p={p} />)}
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-olive/10 bg-white p-3">
            <input
              data-testid="ai-input"
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="اكتب سؤالك..."
              className="flex-1 rounded-full bg-olive/5 px-4 py-3 text-sm outline-none placeholder:text-ink/40"
            />
            <button data-testid="ai-send" type="submit" disabled={busy}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-olive text-ivory transition-colors hover:bg-olive-dark disabled:opacity-50">
              <Send className="h-5 w-5 -scale-x-100" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

const Dot = ({ d = 0 }) => (
  <span className="typing-dot h-2 w-2 rounded-full bg-olive/60" style={{ animationDelay: `${d}s` }} />
);
